import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { normalizeBarcode } from '@loggy/shared';

import { Alert, Button, Field, Inline, Input, Stack, Text } from '../design/index.js';
import styles from './BarcodeScanner.module.css';

export interface BarcodeScannerProps {
  /** Called once, with an already-normalised barcode — from either the live
   *  camera or the typed fallback. The caller owns what happens next; this
   *  component's job ends at handing over a valid code. */
  onDetected: (barcode: string) => void;
  onCancel: () => void;
}

type CameraState =
  | { kind: 'starting' }
  | { kind: 'scanning' }
  | { kind: 'unavailable'; message: string };

/** The shape `decodeFromConstraints` hands back — only `stop` is used here,
 *  so this is all that needs typing without a static import of `@zxing/browser`
 *  (kept dynamic; see the module comment below). */
interface ScannerControls {
  stop: () => void;
}

/**
 * A live camera view that decodes EAN/UPC barcodes as they come into frame,
 * with a typed fallback always beside it — a phone camera can be denied,
 * absent, or just bad at a crumpled label, and the fallback keeps the add
 * flow working when it is.
 *
 * `@zxing/browser` and `@zxing/library` are loaded with a dynamic `import()`
 * so the decoder — a sizeable chunk most sessions never touch, since most
 * foods are typed in or added by search — ships as its own chunk rather than
 * swelling the bundle every visitor downloads.
 */
export function BarcodeScanner({ onDetected, onCancel }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<ScannerControls | null>(null);
  // Guards against acting on a second decode while the first is already
  // being handed to the caller and the camera is on its way down.
  const detectedRef = useRef(false);
  // The latest `onDetected`, read from inside the scan loop set up once on
  // mount below — a ref rather than an effect dependency, so a parent
  // re-render (which hands down a new closure every time, same as
  // `LifeEvents`' inline handlers) never tears the camera down and restarts it.
  const onDetectedRef = useRef(onDetected);
  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  const [cameraState, setCameraState] = useState<CameraState>({ kind: 'starting' });
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);

  /**
   * Stops every track of the current stream and the reader's own scan loop.
   * Called on a successful detect, on cancel, and on unmount — a camera left
   * running after any of those is a privacy bug, not just wasted battery.
   * Safe to call more than once: stopping an already-stopped track is a no-op.
   */
  function stopCamera() {
    controlsRef.current?.stop();
    controlsRef.current = null;
    const stream = videoRef.current?.srcObject;
    if (stream instanceof MediaStream) {
      for (const track of stream.getTracks()) track.stop();
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraState({
          kind: 'unavailable',
          message: 'This browser has no camera access here. Type the number instead.',
        });
        return;
      }

      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);
        if (cancelled) return;

        const hints = new Map<import('@zxing/library').DecodeHintType, unknown>();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
        ]);
        const reader = new BrowserMultiFormatReader(hints);

        const video = videoRef.current;
        if (!video) return;

        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: 'environment' } },
          video,
          (result) => {
            if (detectedRef.current || !result) return;
            // UPC-E normalises to null (see `normalizeBarcode` in
            // shared/src/nodes/foods.ts — it only canonicalises UPC-A, EAN-8
            // and EAN-13/14) so a UPC-E code is ignored here and scanning
            // continues, exactly like a misread that fails its check digit.
            const normalized = normalizeBarcode(result.getText());
            if (!normalized) return;
            detectedRef.current = true;
            stopCamera();
            onDetectedRef.current(normalized);
          },
        );

        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setCameraState({ kind: 'scanning' });
      } catch (err) {
        if (cancelled) return;
        const denied = err instanceof Error && err.name === 'NotAllowedError';
        setCameraState({
          kind: 'unavailable',
          message: denied
            ? 'The camera is not available here, or access was declined. Type the number instead.'
            : 'Could not start the camera here. Type the number instead.',
        });
      }
    }

    void start();
    return () => {
      cancelled = true;
      stopCamera();
    };
    // Deliberately empty: `onDetected` is read through `onDetectedRef` above
    // precisely so this effect — which starts the camera — runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCancel() {
    stopCamera();
    onCancel();
  }

  function handleTypedSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = normalizeBarcode(typed);
    if (!normalized) {
      setTypedError('That is not a valid barcode — check the digits.');
      return;
    }
    detectedRef.current = true;
    stopCamera();
    onDetectedRef.current(normalized);
  }

  return (
    <Stack gap={4} className={styles.scanner} data-testid="barcode-scanner">
      <div className={styles.videoWrap}>
        <video ref={videoRef} playsInline muted className={styles.video} data-testid="barcode-scanner-video" />
        {cameraState.kind === 'starting' && (
          <div className={styles.overlay}>
            <Text size="sm" color="muted">Starting the camera…</Text>
          </div>
        )}
      </div>

      {cameraState.kind === 'unavailable' && (
        <Alert variant="warning" title="Camera unavailable" data-testid="barcode-scanner-unavailable">
          {cameraState.message}
        </Alert>
      )}

      <form onSubmit={handleTypedSubmit} noValidate>
        <Stack gap={3}>
          <Field label="Or type the number under the barcode" error={typedError ?? undefined}>
            <Input
              data-testid="barcode-scanner-typed"
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
                setTypedError(null);
              }}
              inputMode="numeric"
              placeholder="e.g. 8710398503968"
            />
          </Field>
          <Inline gap={3} align="center" wrap>
            <Button type="submit" size="sm" data-testid="barcode-scanner-typed-submit">
              Use this number
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              data-testid="barcode-scanner-cancel"
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}
